/**
 * useAddRoadmapSheetSurface
 *
 * Returns the three `useAddRoadmapItem` surface callbacks
 * (`onConfirmSubmit`, `onSuccess`, `onError`) wired to `ConfirmSheet` /
 * `InfoSheet` using the `l10n.roadmap.*` strings, plus the post-success
 * navigate-back.
 *
 * Mirrors the established `useContactSheetSurface` split: `useAddRoadmapItem`
 * owns state + orchestration and stays presentation-agnostic; this hook is the
 * concrete "sheets" surface choice. Ports the three Flutter surfaces from
 * `add_roadmap_item_screen.dart`:
 *
 *   - confirm → `showConfirmSheet` (`add_roadmap_item_screen.dart:64-79`)
 *   - error   → `showDisplayInfoSheet` ("Error") (`:115-122`)
 *   - success → `showDisplayInfoSheet` ("Idea added") then pop
 *               (`:123-134`; Flutter's `onCloseAction = () => onBackPress()`)
 *
 * Consumers do:
 *
 *     const surface = useAddRoadmapSheetSurface(screenKey);
 *     const { title, description, isSubmitting, canSubmit,
 *             setTitle, setDescription, submit } =
 *         useAddRoadmapItem({ currentUser, ...surface });
 */
import { useCallback, useMemo } from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { InfoSheet } from '@/presentation/components/common/sheet/info/InfoSheet';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { Navigator } from '@/presentation/routing/Navigator';

export interface AddRoadmapSheetSurface {
    onConfirmSubmit: () => Promise<boolean>;
    onSuccess: () => Promise<void>;
    onError: () => Promise<void>;
}

export const useAddRoadmapSheetSurface = (screenKey: string): AddRoadmapSheetSurface => {
    const { l10n } = useLocale();

    const onConfirmSubmit = useCallback(async (): Promise<boolean> => {
        // `ConfirmSheet.show` resolves a response on confirm, or `null` on
        // dismiss/cancel. Treat any non-null response as confirm (Flutter
        // checks `response.confirmed == true`).
        const response = await ConfirmSheet.show({
            title: l10n.roadmap.addIdeaTitle,
            confirmText: l10n.roadmap.submitConfirmMessage,
            okText: l10n.roadmap.addOkButton,
            cancelText: l10n.common.cancel,
            screenKey,
        });
        return response !== null;
    }, [l10n, screenKey]);

    const onSuccess = useCallback(async (): Promise<void> => {
        await InfoSheet.show({
            title: l10n.roadmap.addSuccessTitle,
            infoText: l10n.roadmap.addSuccessMessage,
            okText: l10n.common.close,
            screenKey,
        });
        // Flutter's success sheet `onCloseAction` pops the screen
        // (add_roadmap_item_screen.dart:132).
        Navigator.goBack();
    }, [l10n, screenKey]);

    const onError = useCallback(async (): Promise<void> => {
        await InfoSheet.show({
            title: l10n.roadmap.addErrorTitle,
            infoText: l10n.roadmap.addErrorMessage,
            okText: l10n.common.close,
            screenKey,
        });
    }, [l10n, screenKey]);

    return useMemo(
        () => ({ onConfirmSubmit, onSuccess, onError }),
        [onConfirmSubmit, onSuccess, onError],
    );
};
